import { css, Tooltip } from "@g4rcez/components";
import { CalendarIcon, type Icon } from "@phosphor-icons/react";
import { ChatCircleDotsIcon } from "@phosphor-icons/react/dist/csr/ChatCircleDots";
import { FilesIcon } from "@phosphor-icons/react/dist/csr/Files";
import { FolderSimpleIcon } from "@phosphor-icons/react/dist/csr/FolderSimple";
import { GearIcon } from "@phosphor-icons/react/dist/csr/Gear";
import { HashIcon } from "@phosphor-icons/react/dist/csr/Hash";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/csr/MagnifyingGlass";
import { SidebarIcon } from "@phosphor-icons/react/dist/csr/Sidebar";
import { StarIcon } from "@phosphor-icons/react/dist/csr/Star";
import { TableIcon } from "@phosphor-icons/react/dist/csr/Table";
import { CheckSquareIcon } from "@phosphor-icons/react/dist/csr/CheckSquare";
import { BookmarkIcon } from "@phosphor-icons/react/dist/csr/Bookmark";
import { CalendarDotsIcon } from "@phosphor-icons/react/dist/csr/CalendarDots";
import { TrashSimpleIcon } from "@phosphor-icons/react/dist/csr/TrashSimple";
import { useLocation, useNavigate } from "react-router-dom";
import { useLayoutStore, type ActivityType } from "@/app/contexts/layout-context";
import { useGlobalStore } from "@/store/global.store";
import { uiDispatch, useUIStore } from "@/store/ui.store";
import { WritemeLogo } from "../logo";
import { supportsAI } from "@/lib/platform";

type ActivityIconProps = {
    icon: Icon;
    label: string;
    badge?: number;
    active?: boolean;
    onClick: () => void;
};

const ActivityIcon = ({ icon: Icon, label, badge, active, onClick }: ActivityIconProps) => (
    <Tooltip
        placement="right"
        title={
            <button
                type="button"
                aria-label={label}
                aria-pressed={active}
                title={label}
                onClick={onClick}
                className={css(
                    "writeme-aside-activity-icon relative flex size-8 items-center justify-center rounded-none transition-[background-color,color,transform] duration-150 pointer-coarse:size-11",
                    active
                        ? "writeme-aside-activity-icon--active bg-primary/10 text-primary"
                        : "writeme-aside-activity-icon--inactive text-foreground/45 hover:bg-muted/60 hover:text-foreground",
                )}
            >
                <Icon size={18} strokeWidth={1.5} aria-hidden="true" />
                {active ? (
                    <div className="writeme-aside-activity-indicator absolute -left-2 h-4 w-0.5 rounded-none bg-primary" />
                ) : null}
                {badge !== undefined && badge > 0 && (
                    <span className="writeme-aside-activity-badge absolute top-1 right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground shadow-sm">
                        {badge > 99 ? "99+" : badge}
                    </span>
                )}
            </button>
        }
    >
        {label}
    </Tooltip>
);

export const ActivityBar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [layout, dispatchLayout] = useLayoutStore((state) => ({ activeActivity: state.activeActivity }));
    const [favoritesCount] = useGlobalStore((state) => state.notes.filter((note) => note.favorite).length);
    const [uiState] = useUIStore((state) => ({ sidebarOpen: state.sidebarOpen }));

    const onActivityClick = (activity: ActivityType, shouldCollapse = false) => {
        dispatchLayout.setActivity(activity);
        uiDispatch.setSidebarOpen(!shouldCollapse);
    };

    const onExplorerClick = () => {
        if (layout.activeActivity === "explorer") {
            uiDispatch.toggleSidebar();
        } else {
            dispatchLayout.setActivity("explorer");
            uiDispatch.setSidebarOpen(true);
        }
    };

    return (
        <nav
            className="writeme-aside-activity-bar relative z-10 flex h-full w-12 flex-col items-center border-r border-[color:color-mix(in_srgb,var(--var-color-border)_55%,transparent)] bg-(--wm-rail) py-2 pointer-coarse:w-14 print:hidden"
            aria-label="Workspace navigation"
        >
            <button
                type="button"
                className="writeme-aside-activity-brand mb-4 flex size-8 shrink-0 items-center justify-center rounded-none text-foreground transition-colors hover:bg-muted/60 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Go to home"
                title="Go to home"
                onClick={() => navigate("/")}
            >
                <WritemeLogo className="size-6" aria-hidden="true" />
            </button>
            <div className="writeme-aside-activity-icons flex w-full flex-1 flex-col items-center gap-0.5">
                <ActivityIcon
                    icon={FilesIcon}
                    label="Explorer"
                    onClick={onExplorerClick}
                    active={layout.activeActivity === "explorer"}
                />
                <ActivityIcon
                    label="Search"
                    icon={MagnifyingGlassIcon}
                    onClick={() => onActivityClick("search")}
                    active={layout.activeActivity === "search"}
                />
                <ActivityIcon
                    icon={StarIcon}
                    label="Favorites"
                    badge={favoritesCount}
                    onClick={() => onActivityClick("favorites")}
                    active={layout.activeActivity === "favorites"}
                />
                <ActivityIcon
                    icon={BookmarkIcon}
                    label="Read It Later"
                    onClick={() => {
                        dispatchLayout.setView({ type: "read-it-later" });
                        onActivityClick("read-it-later");
                    }}
                    active={layout.activeActivity === "read-it-later" || location.pathname.startsWith("/read-it-later")}
                />
                <ActivityIcon
                    icon={CalendarDotsIcon}
                    label="Today"
                    active={location.pathname.startsWith("/today")}
                    onClick={() => { navigate("/today"); uiDispatch.setSidebarOpen(false); }}
                />
                <ActivityIcon
                    icon={CheckSquareIcon}
                    label="Open tasks"
                    active={location.pathname.startsWith("/tasks")}
                    onClick={() => { navigate("/tasks"); uiDispatch.setSidebarOpen(false); }}
                />
                <ActivityIcon
                    label="Tags"
                    icon={HashIcon}
                    onClick={() => onActivityClick("tags")}
                    active={layout.activeActivity === "tags"}
                />
                <ActivityIcon
                    label="Groups"
                    icon={FolderSimpleIcon}
                    onClick={() => onActivityClick("groups")}
                    active={layout.activeActivity === "groups"}
                />
                <ActivityIcon
                    label="Calendar"
                    icon={CalendarIcon}
                    active={layout.activeActivity === "calendar"}
                    onClick={() => {
                        onActivityClick("calendar");
                        if (!location.pathname.startsWith("/calendar")) {
                            navigate("/calendar");
                        }
                    }}
                />
                <ActivityIcon
                    label="Views"
                    icon={TableIcon}
                    onClick={() => {
                        onActivityClick("views", true);
                        if (!location.pathname.startsWith("/views")) {
                            navigate("/views");
                        }
                    }}
                    active={layout.activeActivity === "views"}
                />
                <ActivityIcon
                    label="Trash"
                    icon={TrashSimpleIcon}
                    onClick={() => onActivityClick("trash")}
                    active={layout.activeActivity === "trash"}
                />
                <div
                    className="writeme-aside-activity-divider my-1 h-px w-5 shrink-0 bg-[color:color-mix(in_srgb,var(--var-color-border)_70%,transparent)]"
                    aria-hidden="true"
                />
                {supportsAI() && <ActivityIcon
                    icon={ChatCircleDotsIcon}
                    label="Workspace AI"
                    active={location.pathname.startsWith("/chat") || layout.activeActivity === "ai"}
                    onClick={() => {
                        onActivityClick("ai");
                        if (!location.pathname.startsWith("/chat")) {
                            navigate("/chat");
                        }
                    }}
                />}
            </div>
            <div className="writeme-aside-activity-bottom mt-auto flex w-full flex-col items-center gap-0.5">
                <ActivityIcon
                    icon={SidebarIcon}
                    onClick={() => uiDispatch.toggleSidebar()}
                    label={uiState.sidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"}
                />
                <ActivityIcon
                    label="Settings"
                    icon={GearIcon}
                    active={layout.activeActivity === "settings"}
                    onClick={() => {
                        onActivityClick("settings");
                        if (!location.pathname.startsWith("/settings")) {
                            navigate("/settings");
                        }
                    }}
                />
            </div>
        </nav>
    );
};
